import React, { useState } from "react";
import { ButtonType, Experience, ExperienceStatus, Permission, Product, ProductObject } from "types";
import { useDebounce } from "use-debounce";
import { useInfiniteCollection } from "../../../hooks/useInfiniteCollection";
import { InfiniteList } from "../../Common/InfiniteList";
import { RadioInput } from "../../Common/RadioInput";
import { SearchInput } from "../../Common/SearchInput";
import { ButtonItem } from "./ButtonItem";
import { useAuthorization } from "@/hooks/useAuthorization";

interface ProductButtonListProps {
    checkoutType: string;
    popupType: string;
}

export const ProductButtonList = ({ checkoutType, popupType }: ProductButtonListProps) => {
    const [previewType, setPreviewType] = useState(ButtonType.SINGLE);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch] = useDebounce(searchTerm, 1000);
    const { isGranted } = useAuthorization();
    const isGrantedPermission = isGranted(Permission.MEMBERSHIPS_VIEW);

    const fetchParams = {
        "object[in]": "experience,package,combo,membership,partner_package",
        ...(searchTerm.length > 0 ? { "name[contains]": debouncedSearch } : null),
    };

    const {
        data: products,
        isFetching: isFetchingProducts,
        fetchNextPage: fetchNextPageProducts,
        hasNextPage: hasMoreProducts,
    } = useInfiniteCollection<Product>("products", fetchParams);

    const {
        data: partnerExperiences,
        isFetching: isFetchingPartnerExperiences,
        fetchNextPage: fetchNextPagePartnerExperiences,
        hasNextPage: hasMorePartnerExperiences,
    } = useInfiniteCollection<Experience>("experiences", {
        partnerExperiences: true,
        ...(searchTerm.length > 0 ? { "name[contains]": debouncedSearch } : null),
    });

    // We want to show just experience with `ExperienceStatus.PUBLISHED` status.
    const filteredProducts = [...products, ...partnerExperiences].filter((product) => {
        if (product.object !== ProductObject.EXPERIENCE || product?.status === ExperienceStatus.PUBLISHED) {
            if (product.object === ProductObject.MEMBERSHIP && !isGrantedPermission) {
                return false;
            }

            return true;
        }

        return false;
    });

    const isFetching = isFetchingProducts || isFetchingPartnerExperiences;
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    const hasMore = hasMoreProducts || hasMorePartnerExperiences;

    return (
        <div className="space-y-4 px-10">
            <div className="space-y-2.5 px-5">
                <RadioInput
                    checked={previewType === ButtonType.SINGLE}
                    name="buttonType"
                    value={ButtonType.SINGLE}
                    onChange={(e) => setPreviewType(e.target.value)}
                >
                    One button per product
                </RadioInput>

                <RadioInput
                    checked={previewType === ButtonType.ALL_PRODUCTS}
                    name="buttonType"
                    value={ButtonType.ALL_PRODUCTS}
                    onChange={(e) => setPreviewType(e.target.value)}
                >
                    One button for all products
                </RadioInput>
            </div>

            {previewType === ButtonType.SINGLE ? (
                <>
                    <SearchInput
                        value={searchTerm}
                        onChange={(searchResult: string) => setSearchTerm(searchResult.trim())}
                    />
                    {filteredProducts ? (
                        <InfiniteList
                            isLoading={isFetching}
                            hasMore={hasMore}
                            onLoadMore={() => {
                                fetchNextPageProducts();
                                fetchNextPagePartnerExperiences();
                            }}
                        >
                            {filteredProducts.map((product) => (
                                <ButtonItem
                                    key={product.id}
                                    product={product}
                                    checkoutType={checkoutType}
                                    popupType={popupType}
                                    type={previewType}
                                />
                            ))}
                        </InfiniteList>
                    ) : null}
                </>
            ) : (
                <ButtonItem checkoutType={checkoutType} popupType={popupType} type={previewType} />
            )}
        </div>
    );
};
